// Setup-page connection status state machine.
//
// The page auto-probes the secure local HTTPS listener so returning users see
// app status and printers immediately. If the browser blocks the local app
// or the app is not running, the Connect/Retry button gives an explicit user
// action to retry after the user fixes the app or browser trust state.

import {
  bindPrinterSetupHandlers,
  refreshPrintersFromApp
} from "./chit_chats_print_agent_printer_setup";
import {
  runConnectivityProbe,
  getBaseUrl
} from "./chit_chats_print_agent_connectivity";
import {
  statusMessages,
  setBadge,
  setMessage,
  setVersionAndOS,
  setActionButton
} from "./chit_chats_print_agent_setup_status_render";

const STATE_NOT_CONNECTED = "not_connected";
const STATE_CHECKING = "checking";
const STATE_CONNECTED = "connected";
const STATE_FAILED = "failed";
const AUTO_RECONNECT_INTERVAL_MS = 3000;
const CONNECTED_RECHECK_INTERVAL_MS = 30000;

function setStateNotConnected(ctx) {
  setBadge(ctx.container, ctx.messages, "not_running");
  setMessage(ctx.container, ctx.messages.notConnected);
  setVersionAndOS(ctx.container, ctx.messages, null);
  setActionButton(ctx.actionBtn, ctx.messages.connect, "primary");
  ctx.state = STATE_NOT_CONNECTED;
}

function setStateChecking(ctx) {
  setBadge(ctx.container, ctx.messages, "checking");
  setMessage(ctx.container, ctx.messages.checking);
  setVersionAndOS(ctx.container, ctx.messages, null);
  ctx.actionBtn.disabled = true;
  ctx.actionBtn.textContent = ctx.messages.refreshing;
  ctx.state = STATE_CHECKING;
}

function setStateConnected(ctx, probeResult) {
  setBadge(ctx.container, ctx.messages, "running");
  setMessage(ctx.container, ctx.messages.running);
  setVersionAndOS(ctx.container, ctx.messages, probeResult.http && probeResult.http.body);
  setActionButton(ctx.actionBtn, ctx.messages.refresh, "outline");
  ctx.state = STATE_CONNECTED;
  // Explicit connection succeeded — bind printer handlers (idempotent)
  // and trigger a fresh /v1/printers fetch. Always refreshes so a
  // previously-failed fetch doesn't keep an empty list around.
  bindPrinterSetupHandlers();
  refreshPrintersFromApp();
}

function setStateFailed(ctx) {
  setBadge(ctx.container, ctx.messages, "not_running");
  setMessage(ctx.container, ctx.messages.notConnected);
  setVersionAndOS(ctx.container, ctx.messages, null);
  setActionButton(ctx.actionBtn, ctx.messages.retry, "primary");
  ctx.state = STATE_FAILED;
}

function routeProbeResult(ctx, probeResult) {
  if (probeResult.success) {
    setStateConnected(ctx, probeResult);
    return;
  }
  setStateFailed(ctx);
}

function runProbeFlow(ctx) {
  if (ctx.probePromise) {
    return ctx.probePromise;
  }
  setStateChecking(ctx);
  ctx.probePromise = runConnectivityProbe(ctx.baseUrl).then(function(probeResult) {
    routeProbeResult(ctx, probeResult);
    return probeResult;
  }).catch(function(probeErr) {
    if (window.console) {
      window.console.error("chitChatsPrint probe threw", probeErr);
    }
    setStateFailed(ctx);
    return null;
  }).finally(function() {
    ctx.probePromise = null;
  });
  return ctx.probePromise;
}

function bindActionButton(ctx) {
  ctx.actionBtn.addEventListener("click", function() {
    if (ctx.state === STATE_CHECKING) {
      return;
    }
    runProbeFlow(ctx);
  });
}

function canAutoReconnect(ctx) {
  if (!document.body.contains(ctx.container)) {
    return false;
  }
  return ctx.state !== STATE_CHECKING;
}

function autoReconnectInterval(ctx) {
  return ctx.state === STATE_CONNECTED ? CONNECTED_RECHECK_INTERVAL_MS : AUTO_RECONNECT_INTERVAL_MS;
}

function runAutoReconnect(ctx) {
  var now = Date.now();
  var tooSoon = (now - ctx.lastAutoProbeAt) < autoReconnectInterval(ctx);
  if (!canAutoReconnect(ctx) || tooSoon) {
    return;
  }
  ctx.lastAutoProbeAt = now;
  runProbeFlow(ctx);
}

function bindAutoReconnect(ctx) {
  window.addEventListener("focus", function() {
    runAutoReconnect(ctx);
  });
  document.addEventListener("visibilitychange", function() {
    if (document.visibilityState === "visible") {
      runAutoReconnect(ctx);
    }
  });
}

function ensureConnectedBeforePrinterRefresh(ctx) {
  return runProbeFlow(ctx).then(function() {
    // A successful probe enters setStateConnected, which already refreshes
    // printers. Returning false prevents the button path from fetching twice.
    return false;
  });
}

function bindPrinterSetup(ctx) {
  bindPrinterSetupHandlers({
    beforeRefresh: function() {
      return ensureConnectedBeforePrinterRefresh(ctx);
    }
  });
}

function buildContext(container, actionBtn) {
  return {
    container: container,
    actionBtn: actionBtn,
    messages: statusMessages(container),
    baseUrl: getBaseUrl(),
    state: STATE_NOT_CONNECTED,
    probePromise: null,
    lastAutoProbeAt: 0
  };
}

function applyInitialState(ctx) {
  setStateNotConnected(ctx);
  bindPrinterSetup(ctx);
  ctx.lastAutoProbeAt = Date.now();
  runProbeFlow(ctx);
}

function exposeDevHooks(ctx) {
  // Tiny dev/test hook — lets QA force a probe or wipe state without
  // navigating. Not a public API.
  window.ChitChatsPrintSetupPageInternal = {
    runProbe: function() {
      return runProbeFlow(ctx);
    },
    reset: function() {
      setStateNotConnected(ctx);
    }
  };
}

function initStatusPanel() {
  var container = document.querySelector(".js-chit-chats-print-status");
  var actionBtn = document.querySelector(".js-chit-chats-print-status-action");
  if (!container || !actionBtn || !window.fetch || !window.WebSocket) {
    return;
  }
  if (actionBtn.dataset.initialized === "true") {
    return;
  }
  actionBtn.dataset.initialized = "true";

  var ctx = buildContext(container, actionBtn);
  bindActionButton(ctx);
  bindAutoReconnect(ctx);
  applyInitialState(ctx);
  exposeDevHooks(ctx);
}

export { initStatusPanel };
